import { graphql } from '@lib/graphql'
import { execute } from '@lib/graphql/execute'
import { FetchCustomProductTilesWithVariantsQuery } from '@lib/graphql/graphql'
import { QueryConfig } from '@lib/react-query'
import { keepPreviousData, queryOptions, useQuery } from '@tanstack/react-query'
import { CHANNEL, getChannelId } from 'config'
import NodeCachePkg from 'node-cache'
import { ParsedUrlQuery } from 'querystring'

const cache = new NodeCachePkg({ stdTTL: 60, checkperiod: 120 })

export type CustomProductGridTile =
  FetchCustomProductTilesWithVariantsQuery['products']['edges'][number]['node']
// Query for fetching product tiles with all variants
const fetchAllVariantsQuery = graphql(`
  query FetchCustomProductTilesWithVariants(
    $channelId: ID!
    $after: String
    $currency: String
    $query: String
  ) {
    products(
      first: 100
      after: $after
      query: $query
      currency: $currency
      channelId: $channelId
    ) {
      edges {
        node {
          id
          slug
          name
          tags
          metadata {
            key
            value
          }
          attributeAssignments {
            name
            values(first: 10) {
              edges {
                node {
                  __typename
                  value
                  metadata {
                    key
                    value
                  }
                  ... on SwatchAttributeValue {
                    color
                    media {
                      src
                    }
                  }
                }
              }
            }
          }
          variants(first: 10) {
            edges {
              node {
                ...CustomProductGridTileVariant
              }
            }
            pageInfo {
              hasNextPage
              endCursor
            }
          }
        }
      }
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }

  fragment CustomProductGridTileVariant on ProductVariant {
    id
    sku
    name
    media {
      edges {
        node {
          src
          fileName
        }
      }
    }
    metadata {
      key
      value
    }
    selectedAttributes {
      name
      value
    }
    availability {
      availableForPurchase
      availableQuantity
      stockPolicy
    }
    price {
      discountedPrice {
        discount {
          value {
            __typename
            ... on ProductDiscountRelativeValue {
              factor
            }
          }
        }

        value {
          centAmount
          currencyCode
          fractionDigits
        }
      }
      value {
        centAmount
        currencyCode
        fractionDigits
      }
    }
  }
`)

// Query for fetching additional variants of a product
const fetchAdditionalVariantsQuery = graphql(`
  query FetchCustomProductGridAdditionalVariants(
    $id: ID!
    $channelId: ID!
    $currency: String
    $after: String
  ) {
    product(id: $id, channelId: $channelId, currency: $currency) {
      id
      variants(first: 50, after: $after) {
        edges {
          node {
            ...CustomProductGridTileVariant
          }
        }
        pageInfo {
          hasNextPage
          endCursor
        }
      }
    }
  }
`)

// Fetch additional variants for a product
const fetchAllVariantsForProduct = async (
  productId: string,
  channelId: string,
  currency: string,
  intialEndCursor: string,
): Promise<any[]> => {
  let after = intialEndCursor
  const allVariants: any[] = []

  while (true) {
    const response = await execute(fetchAdditionalVariantsQuery, {
      id: productId,
      channelId,
      currency,
      after,
    })

    if (!response.product?.variants) {
      break
    }
    const { edges, pageInfo } = response.product.variants

    allVariants.push(...edges.map((edge) => edge.node))

    if (!pageInfo.hasNextPage) break
    after = pageInfo.endCursor
  }

  return allVariants
}

// Fetch product tiles (recursively handles pagination)
const fetchProductTiles = async (
  channelId: string,
  currency: string,
  query: string | null,
  after: string | null = null,
  collectedTiles: CustomProductGridTile[] = [],
  fetchAdditionalVariants = false,
): Promise<CustomProductGridTile[]> => {
  const queryDocument = fetchAllVariantsQuery

  const response = await execute(queryDocument, {
    channelId,
    after,
    currency,
    query,
  })

  if (!response.products) {
    return collectedTiles
  }
  const { edges, pageInfo } = response.products

  const products = await Promise.all(
    edges.map(async (edge) => {
      const product = edge.node

      // If fetching all variants and there are more, fetch additional variants
      if (product.variants?.pageInfo.hasNextPage && fetchAdditionalVariants) {
        const additionalVariants = await fetchAllVariantsForProduct(
          product.id,
          channelId,
          currency,
          product.variants.pageInfo.endCursor,
        )
        const mergedVariants = [
          ...product.variants.edges.map((edge) => edge.node),
          ...additionalVariants,
        ]
        product.variants.edges = mergedVariants.map((variant) => ({
          node: variant,
        }))
      }

      return product
    }),
  )

  collectedTiles.push(...products)

  if (pageInfo.hasNextPage) {
    return fetchProductTiles(
      channelId,
      currency,
      query,
      pageInfo.endCursor,
      collectedTiles,
    )
  }

  return collectedTiles
}

type CustomProductTilesQueryVariables = {
  channel: CHANNEL
  currency: string
  query: string | null
  urlParams?: ParsedUrlQuery
  fetchAdditionalVariants?: boolean
}

export const getCustomProductTiles = async ({
  channel,
  currency,
  query,
  fetchAdditionalVariants,
}: CustomProductTilesQueryVariables): Promise<CustomProductGridTile[]> => {
  const fetchResult = await fetchProductTiles(
    getChannelId(channel),
    currency,
    query,
    null,
    [],
    fetchAdditionalVariants,
  )
  return fetchResult
}

const getCustomProductTilesQueryOptions = (
  variables: CustomProductTilesQueryVariables,
) => {
  return queryOptions({
    queryKey: ['custom-product-tiles', variables],
    queryFn: () => getCustomProductTiles(variables),
    placeholderData: keepPreviousData,
  })
}

export const useCustomProductTiles = ({
  variables,
  queryConfig,
}: {
  variables?: CustomProductTilesQueryVariables
  queryConfig?: QueryConfig<typeof getCustomProductTilesQueryOptions>
} = {}) => {
  return useQuery({
    ...getCustomProductTilesQueryOptions(variables),
    ...queryConfig,
  })
}
